import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Bell, Plus, Trash2, Pencil, X, Image as ImageIcon, Eye, MousePointerClick, Upload } from "lucide-react";
import { toast } from "sonner";

const PLATFORMS = [
  { key: "web", label: "Web" },
  { key: "mobile", label: "Mobile App" },
];
const AUDIENCES = [
  { key: "anonymous", label: "Anonymous visitors", hint: "Not signed in (web browsing)" },
  { key: "unverified", label: "Registered — unverified", hint: "Signed up, email not verified" },
  { key: "verified", label: "Engaged — verified", hint: "Verified, no booking yet" },
];

const EMPTY = {
  title: "", message: "", image_url: "", cta_label: "", cta_url: "",
  active: true, starts_at: "", ends_at: "",
  platforms: ["web", "mobile"], audiences: ["anonymous", "unverified", "verified"], priority: 0,
};

const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};
const toIso = (local) => (local ? new Date(local).toISOString() : null);

export default function Popups() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // popup object or {} for new
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/popups");
      setItems(data || []);
    } catch { toast.error("Failed to load pop-ups"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (p) => {
    setForm({
      title: p.title || "", message: p.message || "", image_url: p.image_url || "",
      cta_label: p.cta_label || "", cta_url: p.cta_url || "",
      active: !!p.active, starts_at: toLocalInput(p.starts_at), ends_at: toLocalInput(p.ends_at),
      platforms: p.platforms || [], audiences: p.audiences || [], priority: p.priority || 0,
    });
    setEditing(p);
  };
  const close = () => setEditing(null);

  const toggleArr = (key, val) => setForm((f) => {
    const arr = f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val];
    return { ...f, [key]: arr };
  });

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    if (!form.title.trim() || !form.message.trim()) { toast.error("Title and message are required"); return; }
    if (!form.platforms.length) { toast.error("Select at least one platform"); return; }
    if (!form.audiences.length) { toast.error("Select at least one audience"); return; }
    setSaving(true);
    const payload = {
      title: form.title.trim(), message: form.message.trim(),
      image_url: form.image_url.trim() || null,
      cta_label: form.cta_label.trim() || null, cta_url: form.cta_url.trim() || null,
      active: form.active, platforms: form.platforms, audiences: form.audiences,
      priority: Number(form.priority) || 0,
      starts_at: toIso(form.starts_at), ends_at: toIso(form.ends_at),
    };
    try {
      if (editing?.id) {
        await api.patch(`/admin/popups/${editing.id}`, { ...payload, clear_starts_at: !form.starts_at, clear_ends_at: !form.ends_at });
        toast.success("Pop-up updated");
      } else {
        await api.post("/admin/popups", payload);
        toast.success("Pop-up created");
      }
      close(); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/admin/popups/${p.id}`, { active: !p.active });
      setItems((prev) => prev.map((x) => x.id === p.id ? { ...x, active: !x.active } : x));
    } catch { toast.error("Failed to update"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete pop-up "${p.title}"?`)) return;
    try { await api.delete(`/admin/popups/${p.id}`); setItems((prev) => prev.filter((x) => x.id !== p.id)); toast.success("Deleted"); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="admin-popups-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Marketing</p>
          <h1 className="font-display text-3xl text-white uppercase tracking-tight">In-App Pop-Ups</h1>
          <p className="text-sm text-zinc-400 mt-1">Promotional pop-ups shown to <span className="text-white font-bold">leads only</span> — never to clients who have booked. Blocked during workouts, checkout & auth.</p>
        </div>
        <Button onClick={openNew} className="bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest font-bold text-xs" data-testid="popup-new-btn">
          <Plus className="h-4 w-4 mr-2" /> New Pop-Up
        </Button>
      </header>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#27272A] p-12 text-center text-zinc-500" data-testid="popups-empty">
          <Bell className="h-8 w-8 mx-auto mb-2 text-zinc-700" /> No pop-ups yet — create one to promote offers to leads.
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((p) => (
            <div key={p.id} className="border border-[#27272A] bg-[#0A0A0A] p-4 flex items-start gap-4" data-testid={`popup-row-${p.id}`}>
              <div className="w-16 h-16 shrink-0 bg-[#050505] border border-[#27272A] flex items-center justify-center overflow-hidden rounded-sm">
                {p.image_url ? <img src={`${apiBase}${p.image_url}`} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="h-5 w-5 text-zinc-700" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-white font-bold truncate">{p.title}</h3>
                  <button onClick={() => toggleActive(p)} data-testid={`popup-toggle-${p.id}`}
                    className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm border ${p.active ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-zinc-700 bg-zinc-900 text-zinc-500"}`}>
                    {p.active ? "Live" : "Off"}
                  </button>
                </div>
                <p className="text-sm text-zinc-400 line-clamp-2 mt-1">{p.message}</p>
                <div className="flex items-center gap-3 mt-2 flex-wrap text-[10px] uppercase tracking-widest text-zinc-500">
                  <span>{(p.platforms || []).join(" · ") || "—"}</span>
                  <span className="text-zinc-700">|</span>
                  <span>{(p.audiences || []).length === 3 ? "All leads" : (p.audiences || []).join(", ")}</span>
                  <span className="text-zinc-700">|</span>
                  <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />{p.impressions || 0}</span>
                  <span className="inline-flex items-center gap-1"><MousePointerClick className="h-3 w-3" />{p.cta_clicks || 0}</span>
                  <span className="inline-flex items-center gap-1"><X className="h-3 w-3" />{p.dismissals || 0}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => openEdit(p)} className="p-2 text-zinc-400 hover:text-white" data-testid={`popup-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(p)} className="p-2 text-zinc-400 hover:text-[#FF331F]" data-testid={`popup-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-0 sm:p-6 overflow-y-auto" onClick={close}>
          <div className="relative w-full sm:max-w-2xl bg-[#0A0A0A] border border-[#27272A] border-t-4 border-t-[#FF331F] sm:rounded-md my-0 sm:my-8" onClick={(e) => e.stopPropagation()} data-testid="popup-editor">
            <button onClick={close} className="absolute top-3 right-3 p-1.5 bg-black/60 hover:bg-[#FF331F] text-white rounded-sm"><X className="h-4 w-4" /></button>
            <div className="p-6 sm:p-8 space-y-5">
              <h2 className="font-display text-2xl text-white uppercase tracking-tight">{editing?.id ? "Edit Pop-Up" : "New Pop-Up"}</h2>

              <div className="space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Title</label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ready to book your first session?" className="bg-[#050505] border-[#27272A] text-white rounded-sm mt-1" data-testid="popup-field-title" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Message</label>
                  <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Get 6 private Zoom sessions with Coach Chris…" className="bg-[#050505] border-[#27272A] text-white rounded-sm mt-1 min-h-[80px]" data-testid="popup-field-message" />
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Image (optional)</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="/api/uploads/… or full URL" className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="popup-field-image" />
                    <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" />
                    <Button type="button" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} className="border-[#27272A] bg-[#0F0F0F] text-zinc-300 shrink-0" data-testid="popup-upload-btn">
                      <Upload className="h-4 w-4 mr-1" /> {uploading ? "…" : "Upload"}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">CTA Label</label>
                    <Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} placeholder="Book Now" className="bg-[#050505] border-[#27272A] text-white rounded-sm mt-1" data-testid="popup-field-cta-label" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">CTA Link</label>
                    <Input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} placeholder="/book or https://…" className="bg-[#050505] border-[#27272A] text-white rounded-sm mt-1" data-testid="popup-field-cta-url" />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Show on</label>
                  <div className="flex gap-2 mt-1">
                    {PLATFORMS.map((pl) => (
                      <button key={pl.key} type="button" onClick={() => toggleArr("platforms", pl.key)} data-testid={`popup-platform-${pl.key}`}
                        className={`px-3 py-2 text-xs uppercase tracking-widest font-bold rounded-sm border ${form.platforms.includes(pl.key) ? "border-[#FF331F] bg-[#FF331F]/10 text-white" : "border-[#27272A] text-zinc-500"}`}>
                        {pl.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Target lead stage</label>
                  <div className="grid gap-2 mt-1">
                    {AUDIENCES.map((a) => (
                      <button key={a.key} type="button" onClick={() => toggleArr("audiences", a.key)} data-testid={`popup-audience-${a.key}`}
                        className={`text-left px-3 py-2 rounded-sm border ${form.audiences.includes(a.key) ? "border-[#FF331F] bg-[#FF331F]/10" : "border-[#27272A]"}`}>
                        <div className={`text-xs uppercase tracking-widest font-bold ${form.audiences.includes(a.key) ? "text-white" : "text-zinc-500"}`}>{a.label}</div>
                        <div className="text-[10px] text-zinc-600">{a.hint}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Start (optional)</label>
                    <Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="bg-[#050505] border-[#27272A] text-white rounded-sm mt-1" data-testid="popup-field-starts" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">End (optional)</label>
                    <Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="bg-[#050505] border-[#27272A] text-white rounded-sm mt-1" data-testid="popup-field-ends" />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Priority</label>
                    <Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="bg-[#050505] border-[#27272A] text-white rounded-sm w-20" data-testid="popup-field-priority" />
                  </div>
                  <button type="button" onClick={() => setForm({ ...form, active: !form.active })} data-testid="popup-field-active"
                    className={`text-xs uppercase tracking-widest font-bold px-3 py-2 rounded-sm border ${form.active ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-zinc-700 text-zinc-500"}`}>
                    {form.active ? "Active" : "Inactive"}
                  </button>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={save} disabled={saving} className="flex-1 bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-xs h-11" data-testid="popup-save-btn">
                  {saving ? "Saving…" : (editing?.id ? "Save Changes" : "Create Pop-Up")}
                </Button>
                <Button onClick={close} variant="outline" className="border-[#27272A] bg-[#0F0F0F] text-zinc-300 uppercase tracking-widest text-xs h-11">Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
